import React, { useEffect, useState, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from './../axiosInstance.js';
import { localizeArt } from './../localize.js';
import MyUserContext from './../myUserContext.js';

const LogbookHistory = () => {
  const [logbookItems, setLogbookItems] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const myUser = useContext(MyUserContext);
  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get('userId');
  const navigate = useNavigate();

  // For expanding/collapsing notes.
  const [expandedNotes, setExpandedNotes] = useState({});
  const toggleNote = (id) => {
    setExpandedNotes(prev => ({
      ...prev,
      [id]: !prev[id], // toggle only this item's note
    }));
  };

  useEffect(() => {
    let isActive = true;
    const fetchLogbookHistory = async () => {
      const baseParams = new URLSearchParams();
      if (userId) {
        baseParams.set('userId', userId);
      }

      const firstParams = new URLSearchParams(baseParams);
      firstParams.set('count', '10');

      try {
        const firstResponse = await axiosInstance.get(`/logbookHistory?${firstParams.toString()}`);
        if (!isActive) {
          return;
        }
        setLogbookItems(firstResponse.data);

        const secondParams = new URLSearchParams(baseParams);
        secondParams.set('count', '10000');
        secondParams.set('offset', '10');
        setIsLoadingMore(true);
        const secondResponse = await axiosInstance.get(`/logbookHistory?${secondParams.toString()}`);
        setIsLoadingMore(false);
        if (!isActive) {
          return;
        }
        if (Array.isArray(secondResponse.data) && secondResponse.data.length > 0) {
          setLogbookItems((prev) => (prev || []).concat(secondResponse.data));
        }
      } catch (error) {
        setIsLoadingMore(false);
        console.error('Failed to fetch logbook history:', error);
      }
    };

    const fetchUser = () => {
      if (userId) {
        axiosInstance
          .get(`/users/${userId}`)
          .then((response) => {
            setUser(response.data);
          })
          .catch((error) => {
            console.error('Failed to fetch user:', error);
          });
      }
    };

    fetchLogbookHistory();
    fetchUser();
    return () => {
      isActive = false;
    };
  }, [userId]);

  if (logbookItems === null) {
    return (
      <div>
      </div>
    );
  }

  return (
    <div>
      <div className="items-center ml-4 mt-4">
        <img
          src="/back_icon.png"
          alt="Back"
          className="cursor-pointer w-6 h-6"
          onClick={() => navigate(-1)} // Go back in history
        />
      </div>

      <div className="flex flex-col items-center mt-4">

        <h1 className="text-2xl font-bold mb-2 flex items-center justify-center">
          {language === 'ru' ? 'Журнал' : 'Logbook'}
        </h1>
        {user && (
          <div className="flex justify-center mb-6">
            <img
              src={user.avatarUrl}
              alt={user.userName}
              className="w-8 h-8 rounded-full"
            />
          </div>
        )}
        {
        logbookItems.length === 0 &&
        <div class="flex justify-center text-center">
          {language === 'ru' ? 'Пока ничего не добавлено в журнал' : "Nothing added to the logbook yet"}
        </div>
        }
        {isLoadingMore && (
          <div class="flex justify-center text-center">
            <div class="w-full max-w-md">
              <div class="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div class="similars-progress-bar h-full w-1/3 rounded-full bg-gray-300"></div>
              </div>
            </div>
          </div>
        )}
        <div className="w-4/5 md:w-2/3 md:ml-[50%]">
          {
          logbookItems.length === 0 ? (
            <></>
          ) : (
            logbookItems.reduce((acc, item, index, array) => {
              const currentMonth = new Date(item.logTime).getTime() === 0
                ? (language === 'ru' ? 'Давно' : 'Long time ago')
                : new Date(item.logTime).toLocaleString('default', { month: 'long', year: 'numeric' });
              const previousMonth = index > 0 && new Date(array[index - 1].logTime).getTime() === 0
                ? (language === 'ru' ? 'Давно' : 'Long time ago')
                : index > 0
                  ? new Date(array[index - 1].logTime).toLocaleString('default', { month: 'long', year: 'numeric' })
                  : null;

              if (currentMonth !== previousMonth) {
                const itemsInMonth = array.filter((i) => {
                  const itemMonth = new Date(i.logTime).toLocaleString('default', { month: 'long', year: 'numeric' });
                  return itemMonth === currentMonth;
                });

                const movieCount = itemsInMonth.filter((i) => i.art.category === 1).length;
                const videoGameCount = itemsInMonth.filter((i) => i.art.category === 2).length;
                const bookCount = itemsInMonth.filter((i) => i.art.category === 3).length;

                const counts = [];
                if (movieCount > 0) counts.push(`${language === 'ru' ? 'фильмы' : 'movies'}: ${movieCount}`);
                if (videoGameCount > 0) counts.push(`${language === 'ru' ? 'видеоигры' : 'video games'}: ${videoGameCount}`);
                if (bookCount > 0) counts.push(`${language === 'ru' ? 'книги' : 'books'}: ${bookCount}`);

                acc.push(
                  <div key={`month-${index}`} className="mb-4">
                    <div className="text-lg font-bold text-blue-500 mb-2 flex items-center">
                      {currentMonth}
                      {counts.length > 0 && (
                        <span className="text-gray-500 text-sm ml-2 leading-none">
                          ({counts.join(', ')})
                        </span>
                      )}
                    </div>
                  </div>
                );
              } 
              acc.push(
                <div key={index} className="mb-4">
                  <div className="flex items-center">
                    <Link to={`/arts/${item.art.id}`}>
                      <img
                        src={item.art.thumbnailUrl}
                        alt="Thumbnail"
                        className="w-16 h-16 object-cover rounded mr-4"
                      />
                    </Link>
                    <div className="flex flex-grow items-center">
                      <div>
                        <div className="text-sm text-gray-500">
                          {new Date(item.logTime).getTime() === 0
                            ? ""
                            : new Date(item.logTime).toLocaleDateString(language, {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })}
                        </div>
                        <div className="text-sm text-gray-900">
                          {localizeArt(item.art, language).name}
                          {item.finished === true && (
                            <span className="ml-2 text-green-400 text-sm">✓</span>
                          )}
                        </div>
                        {item.artEventId && (
                          <Link to={`/artEvents/${item.artEventId}`}>
                            <img
                              src="/chat_icon.png"
                              className="w-6 h-6"
                            />
                          </Link>
                        )}
                      </div>
                      {item.note && (
                        <div
                          onClick={() => toggleNote(item.id)} // or use index if no id
                          className={
                            `ml-4 p-2 bg-gray-100 text-gray-600 text-xs rounded border border-gray-200 cursor-pointer ` +
                            (expandedNotes[item.id]
                              ? "max-w-[120px] whitespace-normal"
                              : "max-w-[100px] truncate whitespace-nowrap")
                          }
                        >
                          {item.note}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );

              return acc;
            }, [])
          )}
        </div>
      </div>
    </div>
    
  );
};

export default LogbookHistory;
